import {
  Accordion,
  Form,
  Grid,
  List,
  Tab,
} from '@mairistem/composants';
import {
  Exercice,
  getLibelleRechercheImputation,
  ImputationSearchInput,
  LigneMouvement,
  Montants,
  Mouvement,
  Sens,
  TauxTva,
} from '@mairistem/comptabilite';
import { isNil } from 'lodash';

import React from 'react';
import { Icon } from '@mairistem/finances-utils';
import BaseProfilEcritureMouvement
  from '../../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';

import NumberOrString from '../../../../interfaces/common/numberOrString';
import type PanelOptions from '../../../../interfaces/pieces/mouvements/panelOptions';
import LigneMouvementLiaison from './LigneMouvementLiaison';
import LigneMouvementTiers from './LigneMouvementTiers';
import './LignesPiecesControl.less';
import TYPE_COMPTA from '../../../../constants/common/typeCompta';

const TEXT_TYPE_COMPTA = {
  HT: 'HT',
  TTC: 'TTC',
} as const;

interface LignesPiecesControlProps {
  accordionActive: boolean;
  codeBHM: string;
  codesTva: TauxTva[];
  exercice: Exercice;
  identifiantEntite: number;
  isInfiniteList: boolean;
  isLigneOpen: boolean;
  ligne: LigneMouvement;
  loadingCodesTva: boolean;
  onChange: any;
  onDelete: any;
  panelOptions: PanelOptions;
  profilEcriture: BaseProfilEcritureMouvement;
  sens: Sens;
  setModalOpen: (identifiantLigne: number | string) => void;
  setOpenedLignes: React.Dispatch<React.SetStateAction<NumberOrString[]>>;
  setPanelOptions: React.Dispatch<React.SetStateAction<PanelOptions>>;
}

const LignesPiecesControl = ({
  accordionActive,
  codeBHM,
  codesTva,
  exercice,
  identifiantEntite,
  isInfiniteList,
  isLigneOpen,
  ligne,
  loadingCodesTva,
  onChange,
  onDelete,
  panelOptions,
  profilEcriture,
  sens,
  setModalOpen,
  setOpenedLignes,
  setPanelOptions,
}: LignesPiecesControlProps) => {
  const [panelData, setPanelData] = React.useState<Partial<LigneMouvement> & Partial<Mouvement> & Partial<PanelOptions>>();

  const isAccordionShown = isInfiniteList ? isLigneOpen : accordionActive;

  const handleMontantsChange = ({
    montantHT, montantTVA, montantTTC, identifiantCodeTVA,
  }) => {
    onChange?.(ligne?.identifiant, {
      mtht: Number(montantHT).toFixed(2),
      mtva: Number(montantTVA).toFixed(2),
      mttc: Number(montantTTC).toFixed(2),
      identifiantCodeTva: identifiantCodeTVA,
    });
  };

  const handlePanelChange = (changes: object) => onChange?.(ligne?.identifiant, changes);

  const handleImputationChange = (data) => {
    if (!isNil(data)) {
      onChange?.(ligne?.identifiant, {
        identifiantImputation: data?.identifiant,
        imputation: data,
      });
    }
  };

  const handleDelete = () => onDelete?.(ligne?.identifiant);

  // Permet de construire l'objet contenant toutes les données pour les panels
  React.useEffect(() => {
    setPanelData({
      ...ligne,
      ...panelOptions,
      codeBHM,
    });
  }, [
    codeBHM,
    ligne,
    panelOptions,
  ]);

  const panes = [
    {
      menuItem: "Blocs d'informations",
      render: () => (
        <Tab.Pane className="panelTab">
          <List className="panelList">
            {
              profilEcriture?.panels?.map((panel) => {
                if (panel.isVisible(panelData) && !panel.isOnlyMouvement) {
                  return (
                    <List.Item
                      className="panelItem"
                      key={panel.code}
                    >
                      {panel.getComponent(panelData, handlePanelChange, null, sens, setPanelOptions)}
                    </List.Item>
                  );
                }
                return null;
              })
            }
          </List>
        </Tab.Pane>
      ),
    },
    profilEcriture?.isLiaisonVisible && {
      menuItem: 'Liaisons',
      render: () => (
        <LigneMouvementLiaison
          ligneEngagement={ligne?.ligneEngagement}
          ligneFacture={ligne?.ligneFacture}
        />
      ),
    },
    {
      menuItem: 'Tiers mémorisé',
      render: () => (
        <LigneMouvementTiers
          ligne={ligne}
          onChange={handlePanelChange}
          profilEcriture={profilEcriture}
          sens={sens}
        />
      ),
    },
  ];

  const handleLigneAccordionActive = () => {
    setOpenedLignes((old) => (
      // Si la ligne n'est pas déjà dans la liste
      (old.findIndex((id) => id === ligne?.identifiant) === -1)
        // On l'ajoute
        ? [...old, ligne?.identifiant]
        // Sinon on la retire
        : old.filter((id) => id !== ligne?.identifiant)));
  };

  return (
    <>
      <Grid id={`ligne-${ligne?.identifiant}`}>
        <Grid.Row className="ligneRow">
          {isInfiniteList && (
          <Grid.Column
            textAlign="center"
            verticalAlign="middle"
            width={1}
          >
            <Form.Field
              // @ts-expect-error circular
              circular
              control={Icon}
              label=" "
              link
              name={`angle ${isAccordionShown ? 'up' : 'down'}`}
              onClick={handleLigneAccordionActive}
            />
          </Grid.Column>
          )}
          <Grid.Column width={isInfiniteList ? 14 : 15} as={Form}>
            <Form.Group>
              <Form.Field
                control={ImputationSearchInput}
                // @ts-expect-error identifiantEntite
                identifiantEntite={identifiantEntite}
                annee={exercice?.anneeExercice}
                perPage={5}
                sens={sens}
                onChange={handleImputationChange}
                defaultValue={{
                  libelle: getLibelleRechercheImputation(
                    ligne?.imputation?.article?.code,
                    ligne?.imputation?.operation?.code,
                    ligne?.imputation?.fonction?.code,
                    ligne?.imputation?.analytique?.code,
                    ligne?.imputation?.ventilation?.code,
                    ligne?.imputation?.natureBudgetaire,
                  ) ?? null,
                  sens,
                  chapitre: ligne?.imputation?.chapitre ?? null,
                }}
                withMenu
                label="Imputation"
                width={9}
                item={{
                  action: () => setModalOpen(ligne?.identifiant),
                  codes: [73],
                  libelle: 'Alt+i pour ouvrir le menu',
                }}
                natureBudgetaire={ligne?.imputation?.natureBudgetaire ?? profilEcriture?.imputation?.nature}
                articles={profilEcriture?.imputation?.articles}
                chapitre={profilEcriture?.imputation?.chapitre}
                section={profilEcriture?.imputation?.section}
                data-testid="imputationLigne"
                searchComplement={{
                  withAnalytique: true,
                  withArticle: true,
                  withChapitre: true,
                  withFonction: true,
                  withOperation: true,
                  withVentilation: true,
                }}
              />
              <Montants
                onChange={handleMontantsChange}
                montantHT={ligne?.mtht}
                montantTVA={ligne?.mtva}
                montantTTC={ligne?.mttc}
                loadingCodesTva={loadingCodesTva}
                codesTva={codesTva}
                identifiantCodeTVA={ligne?.identifiantCodeTva ?? exercice?.identifiantCodeTva}
                typeComptabilite={(exercice?.typeCompta === TYPE_COMPTA.HT || ligne?.tvaRecup)
                  ? TEXT_TYPE_COMPTA.HT : TEXT_TYPE_COMPTA.TTC}
                tauxTVAProps={{ width: 1 }}
                montantHTProps={{ width: 2 }}
                montantTTCProps={{ width: 2 }}
                montantTVAProps={{ width: 2 }}
                inline
              />
            </Form.Group>
          </Grid.Column>
          <Grid.Column width={1} as={Form} className="removeForm">
            <Form.Field
              control={Icon}
              // @ts-expect-error name
              name="remove"
              link
              color="red"
              circular
              onClick={handleDelete}
              label=" "
              size="large"
              data-testid="supprimeLigne"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {isAccordionShown && (
        <Accordion>
          <Accordion.Content active={isAccordionShown}>
            <Tab
              menu={{ secondary: true, pointing: true }}
              panes={panes}
            />
          </Accordion.Content>
        </Accordion>
      )}
    </>
  );
};

export default LignesPiecesControl;
